import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import ToolManagmentCard from '../../../../../common/element/Card/ToolManagmnetCard';
import {
  getCoachCompaniesByMonth,
  clearCoachCompaniesByMonth,
} from '../../../../../redux/Coach/coachSlice';
import {
  selectCoachCompaniesByMonth,
  selectCoachCompaniesByMonthLoading,
  selectCoachCompaniesByMonthError,
} from '../../../../../redux/Coach/coachSelector';
import { useLocation } from 'react-router-dom';
import ThreeDotBouncingLoader from '../../../../../common/element/ButtonLoader/ThreeDotBouncingLoder';

const CompaniesChart = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const coachId = searchParams.get('id');
  const companiesByMonth = useSelector(selectCoachCompaniesByMonth);
  const loading = useSelector(selectCoachCompaniesByMonthLoading);
  const error = useSelector(selectCoachCompaniesByMonthError);

  useEffect(() => {
    if (coachId) {
      dispatch(getCoachCompaniesByMonth(coachId));
    }

    return () => {
      dispatch(clearCoachCompaniesByMonth());
    };
  }, [dispatch, coachId]);

  // 1. Calculate Chart Logic
  const { chartData, chartCeiling, customTicks } = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const allMonths = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    // Default ticks for empty state: 0, 5, 10... 50
    const defaultTicks = [];
    for (let i = 0; i <= 50; i += 5) {
      defaultTicks.push(i);
    }

    // Default setup if no data
    if (!companiesByMonth) {
      return { 
        chartData: [], 
        chartCeiling: 50, 
        customTicks: defaultTicks
      };
    }

    const dataMap = {};
    companiesByMonth.forEach(item => {
      const key = `${item.year}-${item.month}`;
      dataMap[key] = item.count;
    });

    // Find the highest value
    const maxCount = Math.max(...companiesByMonth.map(item => item.count), 0);
    
    // --- CEILING LOGIC ---
    
    // 1. Determine Ceiling
    // Default is 50. If data > 50, Ceiling is Max + 25.
    let calculatedCeiling = 50;
    if (maxCount > 50) {
      calculatedCeiling = maxCount + 25;
    }

    // 2. Determine Ticks (Y-Axis Labels)
    let ticks = [];
    if (calculatedCeiling === 50) {
       // Default view: 0, 5, 10, 15, ..., 50
       for (let i = 0; i <= 50; i += 5) {
         ticks.push(i);
       }
    } else {
       // Dynamic view: Create ~5 even steps ending at calculatedCeiling
       // We avoid steps of 5 here because if the number is huge (e.g. 500), the axis will be crowded.
       const step = calculatedCeiling / 5;
       ticks = [
         0, 
         Math.round(step), 
         Math.round(step * 2), 
         Math.round(step * 3), 
         Math.round(step * 4), 
         calculatedCeiling
       ];
    }

    // Small visual gap between orange bar and gray background bar
    const gapSize = 2; 

    const processedData = allMonths.map(month => {
      const key = `${currentYear}-${month}`;
      const count = dataMap[key] || 0;
      
      const appliedGap = count > 0 ? gapSize : 0;
      
      // Calculate gray extension
      const extension = Math.max(0, calculatedCeiling - count - appliedGap);

      return {
        month: month.toUpperCase(),
        value: count,
        gap: appliedGap,
        extension: extension,
        actualCount: count,
      };
    });

    return { 
      chartData: processedData, 
      chartCeiling: calculatedCeiling,
      customTicks: ticks 
    };
  }, [companiesByMonth]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
          <p className="font-semibold text-gray-800">{data.month}</p>
          <p className="text-sm text-orange-500">
            Companies: <span className="font-bold">{data.actualCount}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <ToolManagmentCard>
        <div className="mb-6">
          <h2 className="font-lato font-semibold text-[16px] leading-[150%] text-[#101828] text-start">
            Companies Associated
          </h2>
        </div>
        <ThreeDotBouncingLoader/>
      </ToolManagmentCard>
    );
  }

  if (error) {
    return (
      <ToolManagmentCard>
        <div className="flex items-center justify-center h-[400px]">
          <p className="text-red-500">Error loading data</p>
        </div>
      </ToolManagmentCard>
    );
  }

  const totalCompanies = chartData ? chartData.reduce((sum, item) => sum + item.actualCount, 0) : 0;

  return (
    <ToolManagmentCard>
      <div className="mb-6 flex justify-between items-center border-b border-gray-200 pb-4">
        <h2 className="font-lato font-semibold text-[16px] leading-[150%] text-[#101828] text-start">
          Companies Associated
        </h2>
        <div className="text-sm text-gray-600">
          Total: <span className="font-semibold text-orange-500">{totalCompanies}</span> companies
        </div>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
          barGap={0}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fill: '#6b7280', fontSize: 12 }}
            axisLine={{ stroke: '#e5e7eb' }}
            tickLine={false}
          />
          <YAxis
            domain={[0, chartCeiling]} 
            ticks={customTicks}
            tick={{ fill: '#6b7280', fontSize: 12 }}
            axisLine={{ stroke: '#e5e7eb' }}
            tickLine={false}
            tickFormatter={(value) => {
              if (value >= 1000) {
                return value % 1000 === 0 ? `${value / 1000}k` : `${(value / 1000).toFixed(1)}k`;
              }
              return value;
            }}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />
          
          {/* Actual Value - Orange */}
          <Bar
            dataKey="value"
            fill="#fb923c"
            radius={[4, 4, 4, 4]} 
            barSize={20}
            stackId="stack"
          />
          {/* Transparent Gap */}
          <Bar
            dataKey="gap"
            fill="transparent"
            barSize={20}
            stackId="stack"
          />
          {/* Gray Background Extension */}
          <Bar
            dataKey="extension"
            fill="#e5e7eb"
            radius={[4, 4, 0, 0]}
            barSize={20}
            stackId="stack"
          />
        </BarChart>
      </ResponsiveContainer>
    </ToolManagmentCard>
  );
};

export default CompaniesChart;